import { useEffect, useRef, useState } from "react";
import { addProjectMember, ApiError, createProject, friendlyMessage, type GetToken } from "../../controllers/accountApi";
import { useUserDirectory } from "../../controllers/useUserDirectory";
import { useModalDismiss } from "../../controllers/useModalDismiss";
import type { Project, ProjectRole, WorkspaceMember } from "../../models/account";

const PRODUCT_TYPES = ["소프트웨어(SaMD)", "체외진단(IVD)", "전기·기계식 기기", "임플란트/이식형", "진단영상", "기타"];
const DEVICE_CLASSES = ["1등급", "2등급", "3등급", "4등급"];
const MARKETS = ["식약처(MFDS)", "FDA", "CE(MDR)", "PMDA", "NMPA"];
const STANDARDS = ["ISO 13485", "IEC 62304", "ISO 14971", "IEC 60601-1", "ISO 10993"];
const SAFETY_CLASSES = ["A", "B", "C", "해당 없음"];
const ROLE_LABEL: Record<ProjectRole, string> = { PROJECT_ADMIN: "프로젝트 관리자", EDITOR: "에디터", VIEWER: "뷰어" };

// eQMS-style project (medical-device product) registration dialog: project basics + product/regulatory
// info tailored to medical devices + add existing workspace members. On submit it creates the project
// (the creator becomes PROJECT_ADMIN automatically), then adds each selected member. The project key
// is derived server-side from the name (internal identifier, not shown here).
export function CreateProjectWizard({
  getToken, workspaceId, members, currentUserId, onClose, onCreated,
}: {
  getToken: GetToken;
  workspaceId: string;
  members: WorkspaceMember[];
  currentUserId: string | null;
  onClose: () => void;
  onCreated: (project: Project, failedMembers: string[]) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [productType, setProductType] = useState("");
  const [deviceClass, setDeviceClass] = useState("");
  const [markets, setMarkets] = useState<Set<string>>(new Set());
  const [intendedUse, setIntendedUse] = useState("");
  const [standards, setStandards] = useState<Set<string>>(new Set());
  const [safetyClass, setSafetyClass] = useState("");
  const [memberRoles, setMemberRoles] = useState<Record<string, ProjectRole | "">>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useModalDismiss(cardRef, onClose, submitting);

  const dir = useUserDirectory(getToken);
  const addable = members.filter((m) => m.userId !== currentUserId);
  useEffect(() => {
    dir.ensure(members.map((m) => m.userId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  function toggleSet(setter: (fn: (cur: Set<string>) => Set<string>) => void, v: string) {
    setter((cur) => {
      const next = new Set(cur);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  }

  async function submit() {
    if (!name.trim()) { setError("프로젝트 이름을 입력하세요."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const project = await createProject(getToken, workspaceId, {
        name: name.trim(),
        description: description.trim() || undefined,
        productType: productType.trim() || undefined,
        deviceClass: deviceClass || undefined,
        regulatoryMarket: [...markets].join(", ") || undefined,
        intendedUse: intendedUse.trim() || undefined,
        standards: [...standards].join(", ") || undefined,
        safetyClass: safetyClass || undefined,
      });
      const failed: string[] = [];
      for (const [userId, role] of Object.entries(memberRoles)) {
        if (!role) continue;
        try {
          await addProjectMember(getToken, project.id, { userId, role });
        } catch {
          failed.push(dir.label(userId));
        }
      }
      onCreated(project, failed);
    } catch (e) {
      const msg = e instanceof ApiError && e.status === 403
        ? "워크스페이스 관리자만 프로젝트를 생성할 수 있어요."
        : (friendlyMessage(e) || "프로젝트를 만들지 못했습니다.");
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <div className="wz-overlay" role="dialog" aria-modal="true" aria-label="새 프로젝트 생성" onClick={() => { if (!submitting) onClose(); }}>
      <div className="wz-card" ref={cardRef} onClick={(e) => e.stopPropagation()}>
        <div className="wz-head">
          <h2>새 프로젝트 생성</h2>
          <button className="wz-x" aria-label="닫기" onClick={onClose} disabled={submitting}>✕</button>
        </div>

        <div className="wz-body">
          {error && <div className="wz-error" role="alert">{error}</div>}

          <section className="wz-section">
            <h3>기본 정보</h3>
            <label className="wz-field">
              <span className="wz-label">프로젝트 이름 *</span>
              <input className="wz-input" autoFocus maxLength={200} value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 심전도 모니터링 SW" />
            </label>
            <label className="wz-field">
              <span className="wz-label">설명</span>
              <textarea className="wz-input" rows={2} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="프로젝트 개요" />
            </label>
          </section>

          <section className="wz-section">
            <h3>제품 정보 <span className="wz-hint">(의료기기)</span></h3>
            <div className="wz-row2">
              <label className="wz-field">
                <span className="wz-label">제품 유형</span>
                <input className="wz-input" list="pw-types" maxLength={100} value={productType} onChange={(e) => setProductType(e.target.value)} placeholder="예: 소프트웨어(SaMD)" />
                <datalist id="pw-types">{PRODUCT_TYPES.map((v) => <option key={v} value={v} />)}</datalist>
              </label>
              <label className="wz-field">
                <span className="wz-label">의료기기 등급</span>
                <select className="wz-input" value={deviceClass} onChange={(e) => setDeviceClass(e.target.value)}>
                  <option value="">선택 안 함</option>
                  {DEVICE_CLASSES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
            </div>
            <div className="wz-field">
              <span className="wz-label">규제 대상 시장/기관</span>
              <div className="wz-chips">
                {MARKETS.map((v) => (
                  <label key={v} className={`wz-chip ${markets.has(v) ? "on" : ""}`}>
                    <input type="checkbox" checked={markets.has(v)} onChange={() => toggleSet(setMarkets, v)} /> {v}
                  </label>
                ))}
              </div>
            </div>
            <label className="wz-field">
              <span className="wz-label">의도된 사용 목적 (Intended Use)</span>
              <textarea className="wz-input" rows={2} maxLength={2000} value={intendedUse} onChange={(e) => setIntendedUse(e.target.value)} placeholder="이 기기가 무엇을, 누구에게, 어떤 조건에서 사용되는지" />
            </label>
            <div className="wz-row2">
              <div className="wz-field">
                <span className="wz-label">적용 표준</span>
                <div className="wz-chips">
                  {STANDARDS.map((v) => (
                    <label key={v} className={`wz-chip ${standards.has(v) ? "on" : ""}`}>
                      <input type="checkbox" checked={standards.has(v)} onChange={() => toggleSet(setStandards, v)} /> {v}
                    </label>
                  ))}
                </div>
              </div>
              <label className="wz-field">
                <span className="wz-label">SW 안전 등급 <span className="wz-hint">(IEC 62304)</span></span>
                <select className="wz-input" value={safetyClass} onChange={(e) => setSafetyClass(e.target.value)}>
                  <option value="">선택 안 함</option>
                  {SAFETY_CLASSES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
            </div>
          </section>

          <section className="wz-section">
            <h3>멤버 <span className="wz-hint">(선택 — 워크스페이스 멤버를 프로젝트에 추가)</span></h3>
            {addable.length === 0 ? (
              <div className="wz-hint">이 워크스페이스에 추가할 다른 멤버가 없습니다. 먼저 워크스페이스에 초대하세요.</div>
            ) : (
              addable.map((m) => (
                <div className="wz-invite-row" key={m.userId}>
                  <span className="wz-member-id" title={dir.detail(m.userId)}>
                    {dir.detail(m.userId)} <span className="wz-hint">({m.role === "ADMIN" ? "WS 관리자" : "WS 멤버"})</span>
                  </span>
                  <select
                    className="wz-input wz-role"
                    value={memberRoles[m.userId] ?? ""}
                    onChange={(e) => setMemberRoles((cur) => ({ ...cur, [m.userId]: e.target.value as ProjectRole | "" }))}
                  >
                    <option value="">추가 안 함</option>
                    {(["PROJECT_ADMIN", "EDITOR", "VIEWER"] as ProjectRole[]).map((r) => (
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                    ))}
                  </select>
                </div>
              ))
            )}
          </section>
        </div>

        <div className="wz-foot">
          <button className="btn ghost" style={{ width: "auto" }} onClick={onClose} disabled={submitting}>취소</button>
          <button className="btn primary" style={{ width: "auto" }} onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? "만드는 중…" : "프로젝트 만들기"}
          </button>
        </div>
      </div>
    </div>
  );
}
